// Dropdown + start action for org form templates (leads, opportunities, etc.).

import { useEffect, useState } from "react";
import { listOrgFormTemplates } from "@/lib/opportunity/orgFormTemplateClient";
import { createOrgFormInstance } from "@/lib/opportunity/orgFormInstanceClient";
import type { OrgFormSourceType } from "@/lib/opportunity/orgFormInstanceTypes";
import type { OrgFormTemplate } from "@/lib/opportunity/orgFormTemplateTypes";
import { OrgFormFillModal } from "@/components/fulfillment/OrgFormFillModal";

export type OrgFormStartActionProps = {
  sourceType: OrgFormSourceType;
  leadId?: string;
  opportunityId?: string;
  compact?: boolean;
  setToast?: (t: { message: string; type: "success" | "error" } | null) => void;
};

export function OrgFormStartAction(props: OrgFormStartActionProps) {
  const { sourceType, leadId, opportunityId, compact, setToast } = props;
  const [templates, setTemplates] = useState<OrgFormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [creating, setCreating] = useState(false);
  const [fillInstanceId, setFillInstanceId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listOrgFormTemplates().then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) return;
      setTemplates(r.data.templates);
      setSelectedId((prev) => prev || r.data.templates[0]?.id || "");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || templates.length === 0) return null;

  async function handleStart() {
    if (!selectedId) return;
    setCreating(true);
    const r = await createOrgFormInstance({
      form_template_id: selectedId,
      source_type: sourceType,
      lead_id: leadId ?? null,
      opportunity_id: opportunityId ?? null,
    });
    setCreating(false);
    if (!r.ok) {
      setToast?.({ message: r.error, type: "error" });
      return;
    }
    setFillInstanceId(r.data.instance.id);
    setToast?.({ message: "Form started.", type: "success" });
  }

  const selectClass =
    compact
      ? "min-w-0 flex-1 max-w-[9rem] rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-zinc-700 dark:bg-zinc-800"
      : "mt-1 w-full min-w-[10rem] rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800";

  return (
    <>
      <div className={compact ? "flex flex-wrap items-center gap-1.5 min-w-0 max-w-full justify-end" : "flex flex-wrap items-end gap-2"}>
        {!compact ? (
          <label className="min-w-[10rem] flex-1 text-xs">
            <span className="text-gray-500">Org form</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className={selectClass}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className={selectClass}
            title="Select org form"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          disabled={creating || !selectedId}
          onClick={() => void handleStart()}
          className={
            compact
              ? "shrink-0 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              : "rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          }
        >
          {creating ? "Starting…" : compact ? "Form" : "Start form"}
        </button>
      </div>

      {fillInstanceId ? (
        <OrgFormFillModal
          instanceId={fillInstanceId}
          onClose={() => setFillInstanceId(null)}
          onSaved={() => setFillInstanceId(null)}
        />
      ) : null}
    </>
  );
}
